const WATCH_POPUP_TIMEOUT = 100;
const POPUP_WIDTH = 600;

const CANCELED_MESSAGE = 'The Salesforce sign in flow was canceled';

/*
 * Runs the OAuth authorize step in a popup and resolves with the authorization code the popup comes back with.
 * The popup url can only be read once it is back on our own origin, and even then it goes through urls that
 * carry neither a code nor an error, so it is polled until one of them shows up.
 *
 * The window is opened before `buildUrl` runs: popup blockers only allow it while the click that triggered the
 * flow is still being handled, and building the url has to wait on the PKCE challenge being hashed.
 */
export function openOauthPopup(buildUrl: () => Promise<string>): Promise<string> {
    const popup = window.open('', 'Authentication', popupFeatures());

    return new Promise<string>((resolve, reject) => {
        if (!popup) {
            return reject(new Error('The Salesforce sign in window could not be opened'));
        }

        if (typeof popup.focus === 'function') {
            popup.focus();
        }

        const watchPopup = () => {
            try {
                if (popup.closed) {
                    return reject(new Error(CANCELED_MESSAGE));
                }

                const response = parseResponse(popup.location.href);
                if (response) {
                    popup.close();

                    return response.code
                        ? resolve(response.code)
                        : reject(new Error(response.error));
                }
            } catch (ex) {
                // reading the location throws while the popup is still on the salesforce domain
            }

            setTimeout(watchPopup, WATCH_POPUP_TIMEOUT);
        };

        buildUrl().then((url) => {
            if (popup.closed) {
                return reject(new Error(CANCELED_MESSAGE));
            }

            popup.location.href = url;
            setTimeout(watchPopup, WATCH_POPUP_TIMEOUT);
        }, (ex) => {
            popup.close();
            reject(ex);
        });
    });
}

// the params sit in the query string or after the hash route, ie `${origin}/#/login?code=...`
function parseResponse(url: string): { code?: string, error?: string } {
    const code = getParam(url, 'code');
    if (code) {
        return { code };
    }

    const error = getParam(url, 'error_description') || getParam(url, 'error');

    return error ? { error } : null;
}

function getParam(url: string, name: string): string {
    const match = url.match(new RegExp(`[?&]${name}=([^&]*)`));

    return match ? decodeURIComponent(match[1]) : null;
}

function popupFeatures(): string {
    const root = document.documentElement;
    const outerWidth = typeof window.outerWidth !== 'undefined' ? window.outerWidth : root.clientWidth;
    const outerHeight = typeof window.outerHeight !== 'undefined' ? window.outerHeight : root.clientHeight - 22;
    const screenY = typeof window.screenY !== 'undefined' ? window.screenY : window.screenTop;
    let screenX = typeof window.screenX !== 'undefined' ? window.screenX : window.screenLeft;
    screenX = screenX < 0 ? window.screen.width + screenX : screenX;

    const height = Math.floor(outerHeight * 0.8);

    return [
        `width=${POPUP_WIDTH}`,
        `height=${height}`,
        `left=${Math.floor(screenX + (outerWidth - POPUP_WIDTH) / 2)}`,
        `top=${Math.floor(screenY + (outerHeight - height) / 2.5)}`,
        'location=1',
        'toolbar=0'
    ].join(',');
}
